// Split out of src/components/Onboarding.jsx (docs/FILE_SPLIT_RUN1_SPEC.md, move-only).
// Onboarding.jsx re-exports everything it exported before, so existing imports keep working.

import { Icon } from "../Common.jsx";
import { track } from "../../lib/metrics.js";
import { apiGetPushStatus, apiGetVapidKey, apiPushSubscribe, apiPushTest, apiPushUnsubscribe, urlBase64ToUint8Array } from "../../lib/api.js";
import { HOUSEHOLD_CODE_RE } from "../../lib/constants.js";
import { t as ct } from "../../lib/content.js";
import { InstallButton } from "./Install.jsx";
import { IS_NATIVE } from "../../lib/origin.js";

const { useState, useEffect, useCallback, useMemo, useRef, useId } = React;

/* ----------------------------- notifications ------------------------------ */

// Every notification surface needs the same three platform facts, and getting any of them
// wrong produces the two worst outcomes: a raw permission failure on iOS Safari, or a
// control rendered where it can never work. Centralised here so the toggle, both prompts,
// and Settings' attention dot can't drift apart.
export function pushEnvironment() {
  const ua = navigator.userAgent.toLowerCase();
  const isIos = /iphone|ipad|ipod/.test(ua);
  // Chrome, Firefox, Edge and Opera on iOS. They all run WebKit, so they get service
  // workers — but "Add to Home Screen" from them makes a bookmark, not an installed web
  // app, so push is unreachable no matter what the user does. Telling them to install is
  // therefore wrong; telling them to switch to Safari is the only true advice. Previously
  // computed here as `isIosChrome` and then never returned or used at all.
  const isIosOtherBrowser = isIos && /crios|fxios|edgios|opt\//.test(ua);
  const isStandalone = window.matchMedia("(display-mode: standalone)").matches || !!navigator.standalone;
  // Native Run A: the Capacitor build has no service worker and no web push (its push is a
  // later run), whatever the WebView exposes. Every control shows the unsupported state.
  const supported = !IS_NATIVE && "serviceWorker" in navigator && "PushManager" in window && typeof Notification !== "undefined";
  return { isIos, isIosOtherBrowser, isStandalone, supported };
}

// Resolves this device's real push status and keeps it honest over time. The status values
// are the same set NotificationToggle has always used:
//   checking | unsupported | needs-install | denied | off | on | busy
//
// Re-checked on every foreground, not just at mount, because a device that was subscribed
// can silently stop being subscribed: iOS drops push registrations for PWAs left unused for
// a few weeks, and clearing site data drops them anywhere. Without the re-check, Settings
// keeps cheerfully reporting "On for this device" for a device that will never buzz again —
// which is the single most damaging failure mode this feature has, because nobody goes
// looking for a setting that already says it's on.
export function usePushStatus() {
  const [status, setStatus] = useState("checking");
  const statusRef = useRef("checking");
  const { isIos, isIosOtherBrowser, isStandalone, supported } = pushEnvironment();

  useEffect(() => { statusRef.current = status; }, [status]);

  const refresh = useCallback(async () => {
    /* ORDER MATTERS, and it was wrong (Alan, staging, 31 Aug 2026).
     *
     * `supported` requires "PushManager" in window — and on iOS, Safari only exposes
     * PushManager to an INSTALLED web app. In an ordinary Safari tab it simply isn't there,
     * so `!supported` caught every iPhone first and the needs-install line below could never
     * run on the one platform it was written for. Everything built for that state — the
     * "add it to your Home Screen" hint, the install block in the toggle, NotificationPrompt's
     * installFirst handling — was unreachable code.
     *
     * It went unnoticed because "unsupported" used to render nothing at all. The moment the
     * unsupported branch started explaining itself (30 Aug), an iPhone user in Safari was
     * told to open the site in Safari. Alan, in Safari, was.
     *
     * So the iOS install-first case is now decided BEFORE the capability check — but only
     * where installing would actually help. Two carve-outs stay on the unsupported path:
     * third-party iOS browsers, where Add to Home Screen makes a bookmark and no amount of
     * installing will ever get push; and a tab with no service worker at all, which is
     * Private Browsing, where you can't add to the Home Screen either.
     */
    // Native Run A: decided before the iOS install-first case, which is a browser's answer.
    if (IS_NATIVE) { setStatus("unsupported"); return "unsupported"; }
    if (isIos && !isStandalone && !isIosOtherBrowser && "serviceWorker" in navigator) { setStatus("needs-install"); return "needs-install"; }
    if (!supported) { setStatus("unsupported"); return "unsupported"; }
    if (Notification.permission === "denied") { setStatus("denied"); return "denied"; }
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      const next = sub ? "on" : "off";
      setStatus(next);
      return next;
    } catch {
      setStatus("off");
      return "off";
    }
  }, [supported, isIos, isStandalone]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    function onVisible() {
      // Never clobber an in-flight enable/disable: the permission sheet itself can throw a
      // visibilitychange on some Android builds, and refreshing mid-request would flip the
      // toggle back to "off" underneath the user while their answer is still pending.
      if (document.visibilityState !== "visible" || statusRef.current === "busy") return;
      refresh();
    }
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [refresh]);

  return { status, setStatus, refresh, isIos, isStandalone, supported };
}

// Remembered at subscribe time so resyncPushSubscription below knows what role/child this
// device's subscription was registered under. Keyed by household code, like every other
// per-device preference in the app (see HIDDEN_CHILDREN_KEY_PREFIX and friends).
export const PUSH_REG_KEY_PREFIX = "lb_push_reg_";

// Shared by NotificationToggle's own "on" switch and the prompts below, so the actual
// permission-request → subscribe sequence exists in exactly one place rather than being
// forked between a manual toggle and an auto-prompt. Returns the resulting status
// ("on" | "denied" | "off") plus an error message when something failed partway through,
// letting each caller decide how to present that in its own UI.
export async function requestPushSubscription(code, role, childId) {
  try {
    const perm = await Notification.requestPermission();
    if (perm !== "granted") return { status: perm === "denied" ? "denied" : "off", error: null };
    const vapidKey = await apiGetVapidKey();
    if (!vapidKey) return { status: "off", error: "Notifications aren't set up on the server yet." };
    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    // An existing subscription created under a *different* VAPID key makes subscribe() throw
    // InvalidStateError rather than quietly re-keying, which used to surface to the user as a
    // bare "Couldn't turn on notifications." with no way forward. Tear the old one down and
    // try once more instead — this is the normal path after a VAPID key rotation.
    if (sub) {
      const existingKey = sub.options?.applicationServerKey;
      const wanted = urlBase64ToUint8Array(vapidKey);
      const sameKey = existingKey && new Uint8Array(existingKey).length === wanted.length &&
        new Uint8Array(existingKey).every((b, i) => b === wanted[i]);
      if (!sameKey) { try { await sub.unsubscribe(); } catch {} sub = null; }
    }
    if (!sub) {
      sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(vapidKey) });
    }
    const ok = await apiPushSubscribe(code, role, childId, sub);
    if (!ok) return { status: "off", error: "Couldn't save that — try again." };
    try { localStorage.setItem(PUSH_REG_KEY_PREFIX + code, JSON.stringify({ role, childId: childId || null })); } catch {}
    // W2.5: the single choke point every opt-in path funnels through (manual toggle,
    // auto-prompt, wizard step 6), so one event covers them all. Role is the useful
    // split: parent opt-in is what makes the whole request loop work.
    track("notification_optin", { kind: role });
    return { status: "on", error: null };
  } catch (err) {
    return { status: "off", error: err.message || "Couldn't turn on notifications." };
  }
}

// Re-registers this device's existing subscription with the server on app open. The browser
// can hand out a new endpoint without the user doing anything (Chrome rotates them; a
// pushsubscriptionchange event fires in the service worker, which has no idea which
// household this is). Without this, the browser thinks it's subscribed, the toggle agrees,
// and the server is pushing to a dead endpoint — a silent failure nobody can diagnose.
// Cheap and idempotent: the subscribe route de-dupes by endpoint, so re-posting an unchanged
// subscription is a no-op write.
export async function resyncPushSubscription(code) {
  try {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
    const raw = localStorage.getItem(PUSH_REG_KEY_PREFIX + code);
    if (!raw) return; // this device never opted in — nothing to repair
    const { role, childId } = JSON.parse(raw);
    if (role !== "parent" && role !== "child") return;
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return; // genuinely unsubscribed — the toggle will show "off", which is correct
    await apiPushSubscribe(code, role, childId, sub);
  } catch {
    // Best-effort repair — a failure here just leaves things exactly as they already were.
  }
}

// Platform-specific recovery copy for a blocked ("denied") permission. Browsers give no way
// to re-prompt once denied, so the only honest thing to do is say precisely where the switch
// lives on this device rather than the old generic "check your browser or phone Settings".
export function deniedRecoveryHint({ isIos }) {
  if (isIos) return "Blocked on this device. Open iPhone Settings → Notifications → Little Bankers and allow them, then come back here.";
  return "Blocked on this device. Tap the padlock (or ⓘ) next to the address bar → Notifications → Allow, then reload. On an installed app, use your phone's app settings.";
}

// Why this browser can't do notifications at all (Alan, staging, 30 Aug 2026).
//
// He reached the wizard's notification step in an iOS Private Browsing tab and saw a bell,
// a paragraph promising alerts, and no toggle whatsoever — because Private Browsing has no
// service worker, pushEnvironment() reports unsupported, and NotificationToggle's answer to
// unsupported was to render null. Setup then completed with notifications silently never
// set up, which is the failure the whole step exists to prevent, and nothing on screen
// gave him a single clue why.
//
// Rendering nothing is right where a toggle would only be clutter (a child's Earn tab on a
// desktop browser). It is wrong at the one screen whose entire subject is notifications.
// So the explanation exists, and callers opt into showing it.
// Why THIS browser can't do notifications at all. Only reached once the install-first case
// above has been ruled out, so it never tells someone in Safari to open Safari — the exact
// thing it did on Alan's phone before the ordering fix.
export function pushUnsupportedReason() {
  // Native Run A: the app build says so in its own words — none of the browser advice below
  // (open Safari, add to the Home Screen) means anything inside the app.
  if (IS_NATIVE) {
    return ct("onboarding.notifications.unsupported_native", "Notifications are not in the app yet. Everything else works, and you can check the app for new requests.");
  }
  const { isIos, isIosOtherBrowser } = pushEnvironment();
  const noServiceWorker = !("serviceWorker" in navigator);
  if (isIosOtherBrowser) {
    return ct("onboarding.notifications.unsupported_ios_browser", "On iPhone and iPad, only Safari can do notifications. Open littlebankers.uk in Safari and add it to your Home Screen, then turn them on from Settings → Notifications.");
  }
  if (isIos && noServiceWorker) {
    return ct("onboarding.notifications.unsupported_ios_private", "This looks like a Private Browsing tab — iPhone and iPad switch notifications off there. Open littlebankers.uk in a normal Safari tab, add it to your Home Screen, and you can turn notifications on from Settings → Notifications.");
  }
  if (isIos) {
    return ct("onboarding.notifications.unsupported_ios", "This iPhone or iPad can't do notifications — they need iOS 16.4 or newer. Everything else works; you'll just need to check the app for new requests.");
  }
  return ct("onboarding.notifications.unsupported_generic", "This browser can't do notifications. Everything else works — you'll just need to check the app for new requests, or open Little Bankers in another browser to turn them on.");
}

// Reusable push-notification opt-in. `role` is "parent" (gets alerted on every new request
// from any child) or "child" (gets alerted when a parent resolves one of their own requests)
// — childId is required for "child". Renders nothing on unsupported browsers so it never
// clutters the UI where it can't work anyway.
// `asRow` (LB-164): kid Home renders this as a row on the canvas rather than a card; the
// parent-side uses keep the card.
export function NotificationToggle({ code, role, childId, label, forcedOff, forcedOffReason, showUnsupported, onStatusChange, asRow, footer }) {
  const { status, setStatus, refresh, isIos, isStandalone } = usePushStatus();
  // asRow (pass N, Alan): a compact white block, label and toggle side by side, centred on
  // wide screens so they don't drift to opposite edges of a 1000px column.
  const shell = asRow ? "bg-white rounded-lg border border-slate-200 px-4 py-2.5" : "bg-white rounded-2xl p-4 shadow-sm border border-slate-100";
  const [error, setError] = useState("");
  const [testState, setTestState] = useState(null); // null | "sending" | "sent" | "failed"

  // Tell the caller the status the FIRST time it resolves, not just when the toggle is
  // used (Alan, 30 Aug 2026). onStatusChange only ever fired from enable/disable, so a
  // caller that branches on push status — the wizard's final step does exactly that — was
  // stuck on its own initial guess: it offered "Finish without notifications" to a parent
  // who already had them on, and treated a browser that cannot subscribe at all as merely
  // switched off. One report on resolution, then the enable/disable calls below take over.
  const resolvedRef = useRef(false);
  useEffect(() => {
    if (resolvedRef.current) return;
    if (status === "checking" || status === "busy") return;
    resolvedRef.current = true;
    onStatusChange?.(status);
  }, [status]);

  // A parent can mute a specific child's notifications on this specific device (see the
  // device kids setup flow and ProfilesTab's per-device "Notifications" toggle — hiding a
  // child forces this too). If that already left a real push subscription active on this
  // device, tear it down here rather than just hiding the switch — otherwise the device
  // would keep quietly buzzing for a child it's no longer supposed to notify about.
  useEffect(() => {
    if (forcedOff && status === "on") disable();
  }, [forcedOff, status]);

  // Bugfix (Aug 2026): HouseholdCoverageCard, rendered right below this in Settings, used to
  // fetch the server's device count exactly once on mount and never again — so subscribing
  // (or unsubscribing) here left it showing a stale count for the rest of the visit. A
  // parent could watch this switch turn on and still be told "0 devices alerted" right
  // underneath it. onStatusChange fires once an enable/disable actually resolves (success OR
  // failure — a failed enable is still worth a re-check, in case a stale subscription from
  // some earlier attempt is what the server has on file) so the caller can trigger that
  // re-fetch. Optional and a no-op if the caller doesn't need it (e.g. the child-role toggle
  // on Earn, which has no coverage card nearby).
  async function enable() {
    setStatus("busy"); setError(""); setTestState(null);
    const result = await requestPushSubscription(code, role, childId);
    setStatus(result.status);
    if (result.error) setError(result.error);
    onStatusChange?.(result.status);
  }

  async function disable() {
    setStatus("busy"); setError(""); setTestState(null);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) { await apiPushUnsubscribe(code, sub.endpoint); await sub.unsubscribe(); }
      try { localStorage.removeItem(PUSH_REG_KEY_PREFIX + code); } catch {}
      setStatus("off");
      onStatusChange?.("off");
    } catch (err) {
      setError(err.message || "Couldn't turn off notifications.");
      setStatus("on");
      onStatusChange?.("on");
    }
  }

  // "It says it's on but I never get anything" is otherwise unanswerable: permission,
  // subscription, VAPID signing, the push service, and OS-level Focus/Do Not Disturb all sit
  // between this switch and an actual buzz, and only the first two are visible from here.
  // One round trip through the real send path proves or disproves the whole chain.
  async function sendTest() {
    setTestState("sending");
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (!sub) { setTestState("failed"); await refresh(); return; }
      const ok = await apiPushTest(code, sub.endpoint);
      setTestState(ok ? "sent" : "failed");
      if (!ok) await refresh();
    } catch {
      setTestState("failed");
    }
  }

  if (status === "checking") return null;
  if (status === "unsupported") {
    if (!showUnsupported) return null;
    return (
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100" data-testid="push-unsupported">
        <div className="text-sm font-semibold text-slate-700"><Icon id="ic-bell" size={16} className="inline-block align-[-3px] mr-1.5 opacity-40" style={{ color: "currentColor" }} />{label}</div>
        <p className="text-xs text-slate-500 mt-0.5">{pushUnsupportedReason()}</p>
      </div>
    );
  }

  if (forcedOff) {
    return (
      <div className={`${shell} opacity-75`}>
        <div className="text-sm font-semibold text-slate-700"><Icon id="ic-bell" size={16} className="inline-block align-[-3px] mr-1.5 opacity-40" style={{ color: "currentColor" }} />{label}</div>
        <p className="text-xs text-slate-500 mt-0.5">{forcedOffReason || "Notifications are turned off for this on this device."}</p>
      </div>
    );
  }

  return (
    <div className={shell}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-700"><Icon id="ic-bell" size={16} className="inline-block align-[-3px] mr-1.5" style={{ color: "currentColor" }} />{label}</div>
          {status === "needs-install" && <p className="text-xs text-slate-500 mt-0.5">{ct("onboarding.notifications.ios_hint", "iPhone and iPad only allow notifications once the app is on your Home Screen. Add it below, then reopen Little Bankers from the new icon and turn this on.")}</p>}
          {status === "denied" && <p className="text-xs text-rose-500 mt-0.5">{deniedRecoveryHint({ isIos })}</p>}
          {(status === "on" || status === "off" || status === "busy") && <p className="text-xs text-slate-500 mt-0.5">{status === "on" ? ct("onboarding.notifications.on", "On for this device.") : ct("onboarding.notifications.off", "Off for this device.")}</p>}
        </div>
        {(status === "on" || status === "off" || status === "busy") && (
          <button type="button" disabled={status === "busy"} onClick={status === "on" ? disable : enable} aria-pressed={status === "on"}
            aria-label={label}
            className={`flex-shrink-0 inline-block w-11 h-6 rounded-full relative transition-colors ${status === "on" ? "bg-emerald-500" : "bg-slate-200"} ${status === "busy" ? "opacity-60" : ""}`}>
            <span className={`absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${status === "on" ? "translate-x-5" : "translate-x-0"}`} />
          </button>
        )}
      </div>

      {/* The old copy pointed at the Install button "top right" — which renders null on any
          browser that hasn't fired beforeinstallprompt, leaving an instruction pointing at
          nothing. Putting the real button inline means the dead end can't happen. */}
      {status === "needs-install" && (
        <div className="mt-3 flex justify-start"><InstallButton code={code} /></div>
      )}

      {status === "denied" && (
        <button type="button" onClick={refresh} className="mt-3 text-xs font-bold text-slate-500 underline">
          I've allowed it — check again
        </button>
      )}

      {status === "on" && (
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <button type="button" disabled={testState === "sending"} onClick={sendTest}
            className="text-xs font-bold text-slate-600 bg-slate-100 rounded-lg px-3 py-1.5 disabled:opacity-60">
            {testState === "sending" ? "Sending…" : "Send a test notification"}
          </button>
          {testState === "sent" && <span className="text-xs text-emerald-600 font-semibold">Sent — it should appear in a few seconds.</span>}
          {testState === "failed" && <span className="text-xs text-rose-500 font-semibold">Couldn't send. Try turning this off and on again.</span>}
        </div>
      )}

      {/* LB-265: an optional block the caller renders INSIDE this card rather than beside
          it. Settings uses it for "what you'll get", which spent months as a separate card
          at the bottom of the screen, describing a switch two cards above it. */}
      {footer}

      {error && <div className="text-rose-600 text-xs font-semibold mt-2">{error}</div>}
    </div>
  );
}

/* ------------------------ household coverage warning ---------------------- */

// "Is anyone actually being told?" — a question no single device can answer for itself,
// because each one can only see its own subscription. A parent who declined on their phone
// gets a perfectly ordinary-looking app whose requests silently pile up unseen, and nothing
// anywhere points at the cause. This asks the server for the household-wide count and says so
// plainly when it's zero.
//
// Renders nothing while loading, on any fetch failure, and whenever coverage exists — so on
// the overwhelming majority of loads it's invisible. Deliberately not dismissible: it
// disappears the moment it's fixed, and "dismissed" would just restore the silent failure it
// exists to prevent.
export function NotificationCoverageBanner({ code, onOpenSettings }) {
  const [parents, setParents] = useState(null);

  useEffect(() => {
    let cancelled = false;
    // Preview mode runs the whole app against a fake in-memory household under the code
    // "PREVIEW", which isn't a real household code — firing this request would break the
    // "?preview=app makes zero API calls" guarantee that preview mode is built on.
    if (!HOUSEHOLD_CODE_RE.test(code || "")) return undefined;
    apiGetPushStatus(code).then((status) => {
      if (!cancelled && status) setParents(status.parents);
    });
    return () => { cancelled = true; };
  }, [code]);

  const { status } = usePushStatus();
  // Two gates, both deliberate. "unsupported" means the parent is on a browser that could
  // never fix this, so the warning would be pure noise. "checking" means we haven't resolved
  // this device's own state yet — warning about household coverage before we can even read
  // the device in front of us risks a banner that flashes up and then contradicts itself.
  if (status === "checking" || status === "unsupported") return null;
  if (parents === null || parents > 0) return null;

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-2xl px-3.5 py-3 mb-2">
      <div className="text-sm font-bold text-amber-800">{ct("onboarding.notifications.none_heading", "No one is being notified about requests")}</div>
      <p className="text-xs text-amber-700 mt-0.5">
        {ct("onboarding.notifications.none_body", "No device in this household has parent notifications turned on, so requests will sit here until someone opens the app and checks.")}
      </p>
      {onOpenSettings && (
        <button type="button" onClick={onOpenSettings} className="mt-2 bg-pill text-navy font-bold text-xs rounded-lg px-3 py-1.5">
          Turn notifications on
        </button>
      )}
    </div>
  );
}

/* ------------------------- notification opt-in prompts --------------------- */

// A surface asks at most this many times, and waits this long between asks. "Not now" used
// to write a permanent flag, which meant a single mistimed tap — during setup, or with a
// child watching over a shoulder — permanently cost that device its notifications, with the
// only route back being a toggle the user has no reason to know exists. Snoozing instead
// asks again at a later, calmer moment; the ask count stops it becoming nagging.
export const PUSH_PROMPT_MAX_ASKS = 3;
export const PUSH_PROMPT_SNOOZE_MS = 5 * 24 * 60 * 60 * 1000; // 5 days

// Per household+surface, stored as JSON so a snooze can expire rather than being the
// permanent "1" the old lb_seen_notify_prompt_* flag wrote. Those old flags are still
// honoured on read (treated as finished) so nobody who already dismissed the kid popup gets
// re-asked just because they upgraded.
export function readPromptState(key, legacyKey) {
  try {
    if (legacyKey && localStorage.getItem(legacyKey) === "1") return { asks: PUSH_PROMPT_MAX_ASKS, snoozeUntil: 0, done: true };
    const raw = localStorage.getItem(key);
    if (!raw) return { asks: 0, snoozeUntil: 0, done: false };
    const parsed = JSON.parse(raw) || {};
    return { asks: parsed.asks || 0, snoozeUntil: parsed.snoozeUntil || 0, done: !!parsed.done };
  } catch {
    return { asks: 0, snoozeUntil: 0, done: false };
  }
}

export function writePromptState(key, next) {
  try { localStorage.setItem(key, JSON.stringify(next)); } catch {}
}

// The shared modal behind both prompts below. Two states it can show:
//   - "off"           → the real ask, with a Turn on button that requests permission here.
//   - "needs-install" → iOS Safari, where no permission prompt is even possible yet, so the
//                       ask becomes "install first" with a working Install button. The old
//                       code rendered nothing at all in this case, which meant iPhone users
//                       browsing in Safari — a large share of UK families — were never told
//                       notifications existed.
// Anything else (already on, blocked, unsupported, muted) renders nothing, same as before.
function NotificationPrompt({ code, role, childId, forcedOff, storageKey, legacyKey, heading, body, installBody, cta, reaskDays }) {
  const { status, isIos } = usePushStatus();
  const isIosChrome = isIos && /crios/i.test(typeof navigator !== "undefined" ? navigator.userAgent : ""); // was referenced but never defined here (tsc, 12 Sep)
  const [prompt, setPrompt] = useState(() => readPromptState(storageKey, legacyKey));
  const [busy, setBusy] = useState(false);
  const [closed, setClosed] = useState(false);

  function finish() {
    const next = { asks: (expired ? 0 : prompt.asks) + 1, snoozeUntil: 0, done: true, lastAt: Date.now() };
    writePromptState(storageKey, next);
    setPrompt(next);
    setClosed(true);
  }

  function snooze() {
    const asks = (expired ? 0 : prompt.asks) + 1;
    const next = { asks, snoozeUntil: Date.now() + PUSH_PROMPT_SNOOZE_MS, done: asks >= PUSH_PROMPT_MAX_ASKS, lastAt: Date.now() };
    writePromptState(storageKey, next);
    setPrompt(next);
    setClosed(true);
  }

  async function turnOn() {
    setBusy(true);
    await requestPushSubscription(code, role, childId);
    setBusy(false);
    // Granted or blocked, there is nothing left to ask — both are final answers.
    finish();
  }

  // Weekly re-ask (Alan, 28 Aug): with reaskDays set, an exhausted/dismissed prompt state
  // EXPIRES once that many days pass since the last interaction — still off next week means
  // one fresh round of asks, indefinitely, but never more often than the window. "Turn on"
  // resolving to granted/blocked ends re-asks naturally: status leaves "off" either way
  // (blocked reports its own status), and the eligible gate below never fires again.
  const expired = reaskDays > 0 && prompt.lastAt > 0 && Date.now() - prompt.lastAt >= reaskDays * 24 * 60 * 60 * 1000;
  const effective = expired ? { asks: 0, snoozeUntil: 0, done: false, lastAt: prompt.lastAt } : prompt;
  if (expired && prompt.done) { /* state resets lazily on next interaction via finish/snooze */ }
  const eligible = !closed && !forcedOff && !effective.done &&
    effective.asks < PUSH_PROMPT_MAX_ASKS && Date.now() >= effective.snoozeUntil &&
    (status === "off" || status === "needs-install");

  if (!eligible) return null;

  const installFirst = status === "needs-install";

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      {/* role/aria-label are here so this modal is unambiguously targetable — it can now appear
          on iOS Safari (where it previously rendered nothing), which means any future
          mobile-safari Playwright project needs a reliable way to dismiss it rather than
          guessing at div structure. See the dialog-scoping note in 09-undo-reversal.spec.js. */}
      <div role="dialog" aria-label="Turn on notifications" className="bg-white rounded-lg shadow-lg w-full max-w-sm p-6 text-center">
        <div className="mb-2 flex justify-center"><Icon id={installFirst ? "ic-install" : "ic-bell"} size={40} style={{ color: "#1B2A4A" }} /></div>
        <div className="font-extrabold text-slate-900 text-lg mb-2">{installFirst ? "One quick step first" : heading}</div>
        <p className="text-sm text-slate-500 mb-4">{installFirst ? installBody : body}</p>
        {installFirst ? (
          <>
            <div className="flex justify-center mb-3"><InstallButton code={code} /></div>
            <p className="text-xs text-slate-400 mb-2">{isIosChrome ? "In Chrome, tap Share (top right of the address bar), then \"Add to Home Screen\"." : isIos ? "Tap Share ⬆️ in Safari, then \"Add to Home Screen\"." : "Add Little Bankers to your Home Screen, then reopen it from the new icon."}</p>
          </>
        ) : (
          <button disabled={busy} className="w-full bg-slate-900 text-white font-bold text-sm rounded-lg py-2.5 mb-2 disabled:opacity-60" onClick={turnOn}>
            {busy ? "Turning on…" : cta}
          </button>
        )}
        <button disabled={busy} className="w-full text-slate-400 font-semibold text-xs py-1.5" onClick={snooze}>{ct("onboarding.not_now", "Not now")}</button>
      </div>
    </div>
  );
}

// Shown the first time a child opens Kid Mode on a given device — a far more visible on-ramp
// to the same subscription than the toggle further down the Home tab.
export function KidNotificationPrompt({ code, childId, forcedOff }) {
  return (
    <NotificationPrompt
      code={code}
      role="child"
      childId={childId}
      forcedOff={forcedOff}
      // v2 (Alan, Aug 2026): a deliberate ONE-TIME re-ask, not a general re-nag mechanism.
      // The Aug 2026 fixes (stale coverage count, morning-reminder default) mean the
      // notification pipeline now genuinely works and is worth a second impression for
      // anyone who's currently off — bumping the version orphans the old exhausted/dismissed
      // flag so they get a fresh PUSH_PROMPT_MAX_ASKS-worth of tries. Anyone who already has
      // notifications ON never sees this either way, regardless of version — see
      // NotificationPrompt's own `eligible` gate (status === "off" || "needs-install"), so
      // this can't spam someone who's already subscribed. Don't bump this again casually;
      // it exists specifically because this round of fixes earned a re-ask.
      reaskDays={7}
      storageKey={`lb_notify_prompt_v2_${code}_child_${childId}`}
      legacyKey={`lb_seen_notify_prompt_${code}_${childId}`}
      heading="Want to know right away?"
      body="Turn on notifications so you find out the moment your parent approves a reward or an earning activity — no need to keep checking back."
      installBody="Add Little Bankers to your Home Screen and you'll be able to get a buzz the moment a parent replies."
      cta="Turn on notifications"
    />
  );
}

// The parent-side equivalent, and the more important of the two: a request a parent never
// sees is a request that never gets approved, and until now the only route to parent push
// was Parent Mode → PIN → ⚙️ → Notifications → toggle, which almost nobody finds. Mounted by
// App.jsx on entry to Parent Mode, once the wizard and any device-setup flow are out of the
// way, so it lands at a moment the parent is already thinking about running the household.
export function ParentNotificationPrompt({ code }) {
  return (
    <NotificationPrompt
      code={code}
      role="parent"
      childId={null}
      // v2 — see KidNotificationPrompt's matching comment above; same one-time re-ask, same
      // reasoning, kept in step so both surfaces reset together.
      storageKey={`lb_notify_prompt_v2_${code}_parent`}
      heading="Get told when a request comes in"
      body="Your kids' requests wait for you to approve them. Turn on notifications and you'll know the moment one arrives, instead of having to remember to check."
      installBody="Add Little Bankers to your Home Screen and you'll be able to get an alert the moment a child sends a request."
      cta="Notify me about requests"
    />
  );
}
